import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDateParserFormatter, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ContractApiService } from 'src/app/core/api/contract-api.service';
import { CurrencyApiService } from 'src/app/core/api/currency-api.service';
import { TaskApiService } from 'src/app/core/api/task-api.service';
import { Contract } from 'src/app/core/models/contract';
import { ContractCurrency } from 'src/app/core/models/contract-currency';
import { Currency } from 'src/app/core/models/currency';
import { Extension, ExtensionStatus } from 'src/app/core/models/extension';
import { TaskPositionEnum } from 'src/app/core/models/enum/task-position-enum';
import { ProfileContractInfo } from 'src/app/core/models/profile-contract-info';
import { TaskSmall } from 'src/app/core/models/task-small';
import { NgbDateCustomParserFormatter } from 'src/app/core/Util/ngb-date-parser-customer-formatter';
import { convertToDate, convertToNgbDate } from 'src/app/core/Util/util';
import { TranslatePipe } from '@ngx-translate/core';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { NgClass } from '@angular/common';



@Component({
    selector: 'app-task-contract-modal',
    templateUrl: './task-contract-modal.component.html',
    styleUrls: ['./task-contract-modal.component.scss'],
    providers: [{ provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter }],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, NgbInputDatepicker, TranslatePipe, NgClass]
})
export class TaskContractModalComponent implements OnInit {
  @Input() public taskId: number;
  @Input() public contract?: Contract;
  @Input() public viewOnly: boolean = false;

  // Extension mode: reuse this contract form to create/edit a contract Extension.
  // When set, the modal renders the prefilled single-step form and, on save,
  // closes with an Extension payload instead of posting/putting a Contract.
  @Input() public extensionMode: boolean = false;
  @Input() public extensionContractId?: number;
  @Input() public extension?: Extension;
  @Input() public prefillContract?: Contract;
  // Chip shown after the modal title when editing an extension, e.g. "Forlængelse # 1".
  // Swap for a Title field later by passing the title here instead.
  @Input() public extensionLabel?: string;

  public taskContractForm: UntypedFormGroup;

  public taskProfiles: ProfileContractInfo[];
  public selectedProfiles: ProfileContractInfo[] = [];
  public profile: ProfileContractInfo;
  public filledContracts: Contract[] = [];
  public currencies = ["DKK", "USD", "EUR", "SEK", "NOK"]
  public showCurrencyInfo = false;
  public altCurrency = '';
  public altExchangeRate: number | null = null;
  public altPrice = '';
  public availableCurrencies: Currency[] = [];
  private exchangeRateMap: { [code: string]: number } = {};

  public onAltCurrencyChange(currency: string): void {
    this.altExchangeRate = this.exchangeRateMap[currency] ?? null;
  }

  public isFirstStep = true;
  public isLastStep = false;
  public isEditMode = false;
  public isAccountDanish = true;
  public hasByExternalPartner = false;
  public externalPartner: string = "";
  public isForeignCvr = false;
  public months: Array<Number> = [];

  private profileIndex = 0;


  constructor(private activeModal: NgbActiveModal,
    private taskApiService: TaskApiService,
    private contractService: ContractApiService,
    private currencyApiService: CurrencyApiService,
    private formBuilder: UntypedFormBuilder,
    private spinner: SpinnerService,
    private toastrService: ToastrService,) { }

  ngOnInit() {
    this.spinner.show();
    this.setMonths();
    this.buildForm();
    this.currencyApiService.getCurrencies().subscribe(currencies => {
      this.availableCurrencies = currencies;
      this.exchangeRateMap = {};
      currencies.forEach(c => { this.exchangeRateMap[c.code] = c.exchangeRate; });
    });
    if (this.extensionMode) {
      this.isFirstStep = false;
      this.isLastStep = true;
      if (this.prefillContract) {
        this.prefillContractForm(this.prefillContract);
      }
      // Prefill alt-currency box from existing extension
      if (this.extension?.contractCurrency) {
        this.prefillAltCurrency(this.extension.contractCurrency);
      }
    } else if (this.contract) {
      this.isEditMode = true;
      this.isFirstStep = false;
      this.isLastStep = true;
      this.prefillContractForm(this.contract);
      // Prefill alt-currency box from existing contract
      if (this.contract.contractCurrency) {
        this.prefillAltCurrency(this.contract.contractCurrency);
      }
    } else {
      this.loadTaskRelatedCons();
    }
    this.spinner.hide();
  }

  private prefillAltCurrency(cc: ContractCurrency): void {
    this.showCurrencyInfo = true;
    this.altCurrency = cc.currency;
    this.altExchangeRate = cc.exchangeRate;
    this.altPrice = String(cc.price);
  }

  /**Closes the modal */
  public close() {
    this.activeModal.close();
  }

  private setMonths() {
    for (let index = 1; index < 25; index++) {
        this.months.push(index);
    }
  }

  /**Saves and removes the selected profile to array*/
  public onProfileSelected(profile: ProfileContractInfo) {
    this.hasByExternalPartner = false;
    if (this.findProfileById(profile.profileId)) {
      this.selectedProfiles.splice(this.findSelectedProfilesIndex(profile.profileId), 1);
    } else {
      this.selectedProfiles.push(profile);
    }
  }

  public onSaveContractClicked() {
    if (this.taskContractForm.valid) {
      if (this.extensionMode) {
        this.activeModal.close(this.buildExtensionFromForm());
        return;
      }
      if (this.isEditMode) {
        const updatedContract: Contract = new Contract();
        Object.assign(updatedContract, this.taskContractForm.value);
        const startDate: any = updatedContract.startDate;
        const endDate: any = updatedContract.endDate;
        updatedContract.startDate = convertToDate(startDate);
        updatedContract.endDate = convertToDate(endDate);
        updatedContract.id = this.contract.id;
        updatedContract.taskId = this.contract.taskId;
        updatedContract.profileId = this.contract.profileId;
        this.contractService.put(updatedContract).subscribe(() => {
          this.activeModal.close(true);
        });
        return;
      }
      this.addContract();
      try {
        this.contractService.postContracts(this.filledContracts).subscribe(result => {
          this.activeModal.close(true);
        });
      } catch (error) {
        console.error(error);
        this.filledContracts.pop();
        this.profileIndex--;
      }
    }
  }

  /** Maps the contract form values into an Extension payload (extension mode). */
  private buildExtensionFromForm(): Extension {
    const v = this.taskContractForm.value;
    let contractCurrency: ContractCurrency | undefined;
    if (this.showCurrencyInfo && this.altCurrency && this.altPrice && this.altExchangeRate != null) {
      contractCurrency = {
        id: this.extension?.contractCurrency?.id ?? 0,
        extensionId: this.extension?.id ?? 0,
        currency: this.altCurrency,
        price: parseFloat(this.altPrice),
        exchangeRate: this.altExchangeRate,
      };
    }
    return {
      id: this.extension?.id ?? 0,
      contractId: this.extensionContractId ?? this.extension?.contractId ?? 0,
      status: this.extension?.status ?? ExtensionStatus.Draft,
      purchasePrice: v.purchasePrice,
      soldPrice: v.soldPrice,
      travelPrice: v.travelPrice,
      overtimePrice: v.overtimePrice,
      costPrice: v.costPrice,
      margin: v.margin,
      weeklyHours: v.weeklyHours,
      description: v.description,
      reg: v.reg,
      account: v.account,
      iban: v.iban,
      swift: v.swift,
      currency: v.currency,
      equipment: v.equipment,
      option: v.option ? Number(v.option) : undefined,
      startDate: v.startDate ? convertToDate(v.startDate as any) : undefined,
      endDate: v.endDate ? convertToDate(v.endDate as any) : undefined,
      paymentTermsIsKnown: v.paymentTerms === 'yes',
      contractDetailsCount: this.extension?.contractDetailsCount ?? 0,
      contractCurrency,
    };
  }

  /**Makes sure the right fields are shown depending on account type */
  public onAccountTypeChanged(event) {
    var value = event.target.value;
    if (value == 'danish') {
      this.isAccountDanish = true;
      this.taskContractForm.removeControl("iban");
      this.taskContractForm.removeControl("swift");

      this.taskContractForm.addControl("reg", new UntypedFormControl());
      this.taskContractForm.addControl("account", new UntypedFormControl());

      this.taskContractForm.controls["reg"].setValidators([Validators.required, Validators.pattern("^[0-9]*$"), Validators.minLength(4), Validators.maxLength(4)]);
      this.taskContractForm.controls["account"].setValidators([Validators.required, Validators.pattern("^[0-9]*$"), Validators.maxLength(10), Validators.minLength(10)]);
    }
    else if (value == 'foreign') {
      this.isAccountDanish = false;
      this.taskContractForm.removeControl("reg");
      this.taskContractForm.removeControl("account");

      this.taskContractForm.addControl("iban", new UntypedFormControl());
      this.taskContractForm.addControl("swift", new UntypedFormControl());

      this.taskContractForm.controls["iban"].setValidators([Validators.required]);
      this.taskContractForm.controls["swift"].setValidators([Validators.required]);
    }
  }

  public onNextClicked() {
    if (this.hasByExternalPartner) {
      let task = this.createTaskSmall(TaskPositionEnum.Won);
      this.taskApiService.patchSmall(task).subscribe(_ => {
        this.toastrService.success("Opgaven er blevet lukket med ekstern partner")
        this.activeModal.close(true);
      });
    }
    else {
      if (this.selectedProfiles.length > 0) {
        //Makes sure to change step
        if (this.isFirstStep) {
          this.setNextProfile();
          this.isFirstStep = false;
        }
        //If it wasnt first step add contract
        else {
          if (this.taskContractForm.valid) {
            this.addContract();
            this.setNextProfile();
          }
          else this.toastrService.error("Check felterne for manglende udfyldning");
        }
      }
      else this.toastrService.error("Venligst vælg minimum 1 konsulelent");
    }
  }

  public onPrevClicked() {
    //Pop if any filled contracts
    if (this.filledContracts.length > 0) {
      var contract = this.filledContracts.pop();
      this.refillForm(contract);
    }
    this.isLastStep = false;
    //Go back to first step if index is 0
    if (this.profileIndex == 0) {
      this.isFirstStep = true;
      this.selectedProfiles = [];
    }
    else this.profileIndex--;
  }

  private addContract() {
  var contract: Contract = new Contract();
  Object.assign(contract, this.taskContractForm.value);
  var startDate: any = contract.startDate;
  var endDate: any = contract.endDate;

  //This is needed since contract is not the correct type
  contract.startDate = convertToDate(startDate);
  contract.endDate = convertToDate(endDate);
  contract.taskId = this.taskId;

  if (this.selectedProfiles && this.selectedProfiles.length > this.profileIndex) {
    contract.profileId = this.selectedProfiles[this.profileIndex].profileId;
    this.profileIndex++;
  } else {
    console.error('Cannot set profileId for contract: selectedProfiles or profileIndex is undefined');
  }

  if (this.showCurrencyInfo && this.altCurrency && this.altPrice && this.altExchangeRate != null) {
    contract.contractCurrency = {
      id: 0,
      contractId: 0,
      currency: this.altCurrency,
      price: parseFloat(this.altPrice),
      exchangeRate: this.altExchangeRate
    };
  }

  this.filledContracts.push(contract);
}


  /**Finds profile in selectedProfiles array */
  private findProfileById(id: number) {
    var profile = this.selectedProfiles.find((element) => {
      if (element.profileId == id) {
        return true;
      }
      else return false;
    });
    return profile
  }

  private findSelectedProfilesIndex(id: number) {
    var index = this.selectedProfiles.findIndex((element) => {
      if (element.profileId == id) {
        return true;
      }
      else return false;
    });
    return index
  }

  /**Gets the task related consultants for this specific task */
  private loadTaskRelatedCons() {
    this.taskApiService.getProfiles(this.taskId).subscribe(result => {
      this.taskProfiles = result;
    });
  }

  private setNextProfile() {
    if (this.profileIndex < this.selectedProfiles.length - 1) {
      this.profile = this.selectedProfiles[this.profileIndex];

      //Clears and add the current profile
      this.clearForm(this.profile);
    }
    else if (this.profileIndex == this.selectedProfiles.length - 1) {
      this.profile = this.selectedProfiles[this.profileIndex];
      this.clearForm(this.profile);
      this.isLastStep = true;
    }
  }

  public onSelectChange() {
    let cvrInput = this.taskContractForm.get('cvr');
    cvrInput.setValidators([this.cvrValidator()]);
    cvrInput.updateValueAndValidity();
  }

  private cvrValidator() {
    return this.isForeignCvr ? Validators.required : Validators.pattern("^[0-9]{8}$");
  }

  /**Creates the form used for filling contract */
  private buildForm() {
    this.taskContractForm = this.formBuilder.group({
      fullname: '',
      cvr: ['', this.cvrValidator()],
      businessName: '',
      purchasePrice: ['', Validators.pattern("^[0-9]*$")],
      soldPrice: ['', Validators.pattern("^[0-9]*$")],
      travelPrice: ['', Validators.pattern("^[0-9]*$")],
      overtimePrice: ['', Validators.pattern("^[0-9]*$")],
      costPrice: ['', Validators.pattern("^[0-9]*$")],
      currency: ['DKK'],
      margin: '',
      startDate: '',
      endDate: '',
      weeklyHours: '',
      description: '',
      accountType: '',
      paymentTerms: '',
      equipment: '',
      reg: ['', Validators.pattern("^[0-9]*$")],
      account: ['', Validators.pattern("^[0-9]*$")],
      startupPackage: "",
      option: '',
    });
  }

  private prefillContractForm(contract: Contract) {
    if (contract.accountType === 'foreign') {
      this.isAccountDanish = false;
      this.taskContractForm.removeControl('reg');
      this.taskContractForm.removeControl('account');
      this.taskContractForm.addControl('iban', new UntypedFormControl(contract.iban, [Validators.required]));
      this.taskContractForm.addControl('swift', new UntypedFormControl(contract.swift, [Validators.required]));
    }
    this.taskContractForm.patchValue({
      fullname: contract.fullname,
      cvr: contract.cvr,
      businessName: contract.businessName,
      purchasePrice: contract.purchasePrice,
      soldPrice: contract.soldPrice,
      travelPrice: contract.travelPrice,
      overtimePrice: contract.overtimePrice,
      costPrice: contract.costPrice,
      currency: contract.currency,
      margin: contract.margin,
      startDate: convertToNgbDate(new Date(contract.startDate)),
      endDate: convertToNgbDate(new Date(contract.endDate)),
      weeklyHours: contract.weeklyHours,
      description: contract.description,
      accountType: contract.accountType,
      paymentTerms: contract.paymentTerms,
      equipment: contract.equipment,
      startupPackage: contract.startupPackage,
      option: contract.option,
      ...(contract.accountType !== 'foreign' ? { reg: contract.reg, account: contract.account } : {}),
    });
  }

  /**Clears the form and add profile value */
  private clearForm(profile: ProfileContractInfo) {
    this.taskContractForm.reset();
    this.taskContractForm.patchValue({
      fullname: profile.fullname,
      cvr: profile.cvr,
      businessName: profile.businessName,
      reg: profile.reg,
      account: profile.account,
      accountType: profile.accountType,
      currency: 'DKK',
      paymentTerms: 'no',
      startupPackage: 'no',
      equipment: 'own'
    })

    if (profile.accountType == "danish") {
      this.taskContractForm.patchValue({
        reg: profile.reg,
        account: profile.account,
      })
    } else if (profile.accountType == "foreign") {
      this.taskContractForm.patchValue({
        iban: profile.reg,
        swift: profile.account,
        });
  }
}

  private createTaskSmall(taskPosition: TaskPositionEnum) {
    var taskSmall = new TaskSmall();
    taskSmall.id = this.taskId;
    taskSmall.taskPosition = taskPosition;
    taskSmall.wonExternalPartner = this.externalPartner;
    return taskSmall;
  }

  //Refills form in case user goes back
  private refillForm(contract: Contract) {
    this.taskContractForm.reset();
    this.taskContractForm.patchValue({
      fullname: contract.fullname,
      cvr: contract.cvr,
      businessName: contract.businessName,
      purchasePrice: contract.purchasePrice,
      soldPrice: contract.soldPrice,
      travelPrice: contract.travelPrice,
      overtimePrice: contract.overtimePrice,
      costPrice: contract.costPrice,
      currncy: contract.currency,
      margin: contract.margin,
      startDate: contract.startDate,
      endDate: contract.endDate,
      weeklyHours: contract.weeklyHours,
      description: contract.description,
      reg: contract.reg,
      account: contract.account,
      paymentTerms: contract.paymentTerms,
      equipment: contract.equipment,
      option: contract.option
    });
  }
}
